import React, { useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { SUB_PATHS } from '@/constants/urlConstants';
import { useModelSummaryViews, useModelView } from '@mpm-druid/api';
import { MODEL_DASHBOARD_DEFAULT_VIEW_REFRESH_INTERVAL } from '@mpm-druid/constants';
import { ModelView } from '@mpm-druid/types';
import { generateDefaultView } from '@mpm-druid/utils';
import { SmallLoader } from '@shared/components';
import { DashboardContent } from './DashboardContent';
import { DashboardHeader } from './DashboardHeader';
import { useModelViewStore } from './modelViewStore';

import styles from './DashboardPage.module.scss';

export function DashboardPage() {
  const isViewInitialized = useModelViewStore(state => state.isViewInitialized);
  const storedViewId = useModelViewStore(state => state.view.id);
  const setView = useModelViewStore(state => state.setView);
  const cancelAutoSave = useModelViewStore(state => state.cancelAutoSave);
  const flushAutoSave = useModelViewStore(state => state.flushAutoSave);

  const history = useHistory();
  const { modelId, viewId, workspace: workspaceName } = useParams<{
    modelId: string;
    viewId?: string;
    workspace: string;
  }>();
  const {
    data: modelSummaryViews,
    isLoading: isLoadingModelSummaryViews
  } = useModelSummaryViews(
    { modelId },
    {
      keepPreviousData: true,
      refetchInterval: MODEL_DASHBOARD_DEFAULT_VIEW_REFRESH_INTERVAL,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true
    }
  );
  const { isLoading: isLoadingModelView } = useModelView(
    { modelId, viewId },
    {
      keepPreviousData: true,
      refetchInterval: MODEL_DASHBOARD_DEFAULT_VIEW_REFRESH_INTERVAL,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
      onSuccess: (data: ModelView) => {
        const { savingViewId } = useModelViewStore.getState();

        if (savingViewId === data.id) return;

        setView(data);
      }
    }
  );

  const generatedView = useMemo(() => generateDefaultView(modelId), [modelId]);

  useEffect(() => {
    if (viewId || isViewInitialized) return;

    setView(generatedView);
  }, [generatedView, isViewInitialized, setView, viewId]);

  // Auto-save on change page
  useEffect(() => {
    return () => {
      flushAutoSave();
    };
  }, [flushAutoSave]);

  // Validate and redirect to valid view id
  useEffect(() => {
    if (isLoadingModelSummaryViews) return;

    const basePath = `/${workspaceName}/${SUB_PATHS.MODEL_PRODUCTION_MONITORING}/${modelId}/dashboard`;

    // Redirect to stored view id if no view id is provided
    if (!viewId && storedViewId) {
      history.push(`${basePath}/${storedViewId}`);
      cancelAutoSave();
      return;
    }

    // Redirect to default view if no view id is provided
    const defaultViewId = modelSummaryViews?.find(view => view.is_default)?.id;
    if (!viewId && defaultViewId) {
      history.push(`${basePath}/${defaultViewId}`);
      cancelAutoSave();
      return;
    }

    // Redirect to valid view id
    const hasView = modelSummaryViews?.some(view => view.id === viewId);
    if (viewId && !hasView) {
      if (defaultViewId) {
        history.push(`${basePath}/${defaultViewId}`);
      } else {
        history.push(basePath);
        setView(generatedView);
      }
      cancelAutoSave();
    }
  }, [
    cancelAutoSave,
    generatedView,
    history,
    isLoadingModelSummaryViews,
    modelId,
    modelSummaryViews,
    setView,
    storedViewId,
    viewId,
    workspaceName
  ]);

  if (isLoadingModelView || isLoadingModelSummaryViews) {
    return <SmallLoader />;
  }

  return (
    <div className={styles.dashboardTab}>
      <div className={styles.dashboardHeader}>
        <DashboardHeader />
      </div>
      <DashboardContent />
    </div>
  );
}
